import { cn } from '@/lib/utils';
import Image, { ImageProps } from 'next/image';
import React from 'react';

type DynamicImageProps = ImageProps & {
  className?: string;
  src: string;
  alt: string;
  width?: number;
  height?: number;
  style?: React.CSSProperties;
  sizes?: string;
  onLoadingComplete?: () => void;
};

const DynamicImage = ({
  src,
  alt,
  width,
  height,
  className,
  onLoadingComplete,
  style,
  sizes,
  ...props
}: DynamicImageProps) => {
  const blurDataURL = `data:image/svg+xml;base64,${toBase64(shimmer(width || 700, height || 475))}`;
  const [error, setError] = React.useState<React.SyntheticEvent<HTMLImageElement, Event> | null>(
    null
  );
  const [isLoading, setIsLoading] = React.useState(true);

  const imageSrc = src || blurDataURL;

  React.useEffect(() => {
    setError(null);
  }, [src]);

  return (
    <Image
      className={cn(className, isLoading && 'bg-gray-200 opacity-0')}
      src={error ? blurDataURL : imageSrc}
      alt={alt}
      width={width}
      height={height}
      sizes={sizes}
      placeholder='blur'
      style={style}
      blurDataURL={blurDataURL}
      priority={true}
      onError={(event) => setError(event)}
      onLoadingComplete={(e) => {
        setIsLoading(false);
        e.classList.remove('opacity-0');
        onLoadingComplete?.();
      }}
      {...props}
    />
  );
};

const shimmer = (w: number, h: number) => `
<svg width="${w}" height="${h}" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
  <rect width="${w}" height="${h}" fill="#f3f4f6" />
  <rect id="r" width="${w}" height="${h}" fill="url(#g)" />
  <animate xlink:href="#r" attributeName="x" from="-${w}" to="${w}" dur="1s" repeatCount="indefinite" />
</svg>
`;

const toBase64 = (str: string) =>
  typeof window === 'undefined' ? Buffer.from(str).toString('base64') : window.btoa(str);

export default DynamicImage;
